// packages/kb-view/src/image-reference.ts
//
// LAT-2081 (+ MEDIA, M6) — the one rule for WHAT a markdown image reference
// addresses, plus the contract by which a surface turns that into something to
// render (ADR 0064 §3, PRD D7).
//
// A picture referenced from a page addresses a catalog path exactly as a link
// does, so the resolution itself is `./kb-link-target`'s (LAT-1867) — the same
// segment stack, the same single percent-decode, the same refusal to clamp a
// target that climbs above the KB root. What is specific to an image is the
// classification wrapped around it:
//
//   * an EXTERNAL reference (`https:`, `data:`, `//host`, `#frag`, any scheme)
//     is handed back untouched, so the browser keeps doing exactly what it
//     does today — this package never mints a KB path from one;
//   * a QUERY STRING is not part of the path. A link never carries one, but an
//     authored image reference routinely does (`?v=2` cache-busters, size
//     hints from other tools), so it is stripped with the fragment.
//
// Where the URL comes from is deliberately NOT here. `MarkdownView` takes a
// `resolveImage` prop and the surface owns it: the application maps a path to
// the authenticated content route (`?rendition=medium`), and the published
// surface will map the same path to the public asset route (M12). That is why
// {@link MarkdownImageResolver} lives beside the rule rather than inside the
// view — it is the seam, and both halves are written against this file.
//
// React-free entry point (`@atmina/kb-view/image-reference`), like its
// `./kb-link-target` neighbour: a resolver is a pure function and a consumer
// building one must not have to pull React into its program.

import {
  resolveKbLinkPath,
  safePercentDecode,
  type InvalidKbLinkTargetReason,
} from "./kb-link-target";

/**
 * What a markdown image reference addresses. `external` is "not ours" — the
 * surface leaves it to the browser; `path` is a canonical KB path to look up
 * in the catalog; `invalid` carries the reason no path exists, so a surface
 * can name it in a broken-reference marker.
 */
export type ImageReferenceTarget =
  | { kind: "external" }
  | { kind: "path"; path: string }
  | { kind: "invalid"; reason: InvalidKbLinkTargetReason };

/**
 * What a surface decided to do with one reference. `image` renders a picture
 * at `url`; `broken` renders the broken-reference marker naming `path` — the
 * NORMALISED path, so a reader sees what the page actually asked for rather
 * than what it typed. `image` carries `path` too, because a rendition can be
 * unavailable at fetch time (ADR 0064 §3 produces them lazily) and the view
 * falls back to the same marker on the `<img>` error path.
 */
export type MarkdownImageResolution =
  | { kind: "image"; url: string; path: string }
  | { kind: "broken"; path: string };

/**
 * The `MarkdownView` image seam. Called with one reference exactly as authored
 * in the page body; the surface closes over the page path (the resolution is a
 * function of both) and over whatever it needs to mint a URL.
 *
 * Returning `null` means "not mine": the view emits the plain `<img>`
 * react-markdown would have emitted, which is how an absolute external image
 * keeps today's behaviour byte for byte.
 *
 * NEVER called with an empty reference, and a surface therefore needs no rule
 * for one. react-markdown sanitises `img.src` before any override runs and its
 * allowlist is http(s)/mailto/xmpp/irc, so a `data:` image — like an authored
 * `![x]()` — reaches the view as `""`; the view keeps those on today's plain
 * `<img>` rather than asking a resolver to name a path that does not exist.
 */
export type MarkdownImageResolver = (
  reference: string,
) => MarkdownImageResolution | null;

/**
 * Classify one image reference authored in `pagePath`. Pure; no catalog, no
 * transport, no URL policy — the caller owns all three.
 */
export function resolveImageReference(
  reference: string,
  pagePath: string,
): ImageReferenceTarget {
  if (reference === "") return { kind: "invalid", reason: "empty" };
  if (
    SCHEME_RE.test(reference) ||
    reference.startsWith("//") ||
    reference.startsWith("#")
  ) {
    return { kind: "external" };
  }
  // Fragment AND query come off before resolution: whichever appears first
  // ends the path. `?v=2#fig-1` and `#fig-1?x` both address `…` alone.
  const pathPart = reference.split(/[?#]/, 1)[0] ?? "";
  if (pathPart === "") return { kind: "invalid", reason: "empty" };
  return resolveKbLinkPath(safePercentDecode(pathPart), pagePath);
}

// A reference is external when it carries a scheme (`http:`, `data:`,
// `mailto:`, …) or is protocol-relative (`//host/…`). Byte-identical to the
// link resolver's test (`apps/web/src/lib/kb-links.ts`) so one authored
// reference is classified the same way whether it is a link or a picture.
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;
